import React from 'react';
import { Shield, Activity, Code, Check } from 'lucide-react';

const Services = () => {
  return (
    <div className="bg-white">
      {/* Header */}
      <div className="bg-primary py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-extrabold text-white sm:text-5xl">Our Services</h1>
          <p className="mt-4 text-xl text-slate-300 max-w-2xl mx-auto">
            Specialized engineering and software solutions tailored for mission-critical industries.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 space-y-24">
        
        {/* Reliability Assessment */}
        <div id="reliability" className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
          <div>
            <div className="flex items-center space-x-4 mb-6">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Shield className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900">Reliability Assessment</h2>
            </div>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              We provide comprehensive reliability engineering services to ensure your systems meet the highest standards of performance and safety. Our approach integrates traditional methodologies with modern data analysis.
            </p>
            <ul className="space-y-4">
              {[
                'Fault Tree Analysis (FTA)',
                'Failure Mode, Effects, and Criticality Analysis (FMECA)',
                'Reliability Block Diagrams (RBD)',
                'Event Sequence Diagrams (ESD)',
                'System Theoretic Process Analysis (STPA)',
                'Root Cause Analysis'
              ].map((item, i) => (
                <li key={i} className="flex items-start">
                  <Check className="h-6 w-6 text-accent flex-shrink-0 mr-3" />
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-10 lg:mt-0 bg-slate-50 p-8 rounded-2xl border border-slate-100 h-full flex items-center justify-center">
            {/* Placeholder for a diagram or image */}
            <div className="text-center text-slate-400">
               <Shield className="h-32 w-32 mx-auto opacity-20" />
               <p className="mt-4 font-medium">Reliability Modeling Diagram</p>
            </div>
          </div>
        </div>

        {/* Risk Analysis */}
        <div id="risk" className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
          <div className="order-2 lg:order-1 bg-slate-50 p-8 rounded-2xl border border-slate-100 h-full flex items-center justify-center">
             {/* Placeholder for a diagram or image */}
             <div className="text-center text-slate-400">
               <Activity className="h-32 w-32 mx-auto opacity-20" />
               <p className="mt-4 font-medium">Risk Matrix Visualization</p>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <div className="flex items-center space-x-4 mb-6">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Activity className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900">Risk Analysis</h2>
            </div>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              Our risk analysis services help organizations identify, evaluate, and mitigate risks in complex systems. We specialize in advanced probabilistic methods and hybrid causal models.
            </p>
            <ul className="space-y-4">
              {[
                'Probabilistic Risk Assessment (PRA)',
                'Bayesian Belief Networks (BBN)',
                'Hybrid Causal Logic (HCL) Modeling',
                'Hazard Identification & Ranking',
                'Functional Safety Analysis (ISO 26262)',
                'Human Reliability Analysis'
              ].map((item, i) => (
                <li key={i} className="flex items-start">
                  <Check className="h-6 w-6 text-accent flex-shrink-0 mr-3" />
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Software Development */}
        <div id="software" className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
          <div>
            <div className="flex items-center space-x-4 mb-6">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Code className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900">Software Development</h2>
            </div>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              We build robust, scalable, and secure software solutions. With deep expertise in both engineering and software development, we bridge the gap between technical requirements and reliable implementation.
            </p>
            <ul className="space-y-4">
              {[
                'Full-Stack Development (React, Node.js, Python, C#, Java)',
                'Cloud-Native Solutions (AWS, Oracle Cloud)',
                'Microservices Architecture',
                'Agile Project Management',
                'Automated Testing (Selenium, Coded UI)',
                'CI/CD Pipeline Implementation'
              ].map((item, i) => (
                <li key={i} className="flex items-start">
                  <Check className="h-6 w-6 text-accent flex-shrink-0 mr-3" />
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-10 lg:mt-0 bg-slate-50 p-8 rounded-2xl border border-slate-100 h-full flex items-center justify-center">
             {/* Placeholder for a diagram or image */}
             <div className="text-center text-slate-400">
               <Code className="h-32 w-32 mx-auto opacity-20" />
               <p className="mt-4 font-medium">Software Architecture Diagram</p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Services;
